import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { toast } from "sonner";
import { ClipboardCheck, Loader } from "lucide-react";
import VehicleInspectionCapture, { inspectionPhotosComplete, inspectionPhotosPayload } from "./VehicleInspectionCapture";
import InspectionReport from "./InspectionReport";
import InspectionQrHandoff from "./InspectionQrHandoff";
import { formatInspectionSubmitError } from "../lib/bookingErrors";
import { pageItems } from "../lib/pageItems";

export default function TripInspectionPanel({ booking, onDone, asHost = false }) {
  const [inspections, setInspections] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [photos, setPhotos] = useState({});
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [qrToken, setQrToken] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/bookings/${booking.booking_id}/inspections`, { withCredentials: true });
      setInspections(pageItems(r.data));
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, [booking.booking_id]);

  const pickup = inspections.find(i => i.phase === "pickup");
  const ret = inspections.find(i => i.phase === "return");
  const needsPickup = !asHost && booking.status === "confirmed" && !pickup;
  const needsReturn = booking.status === "active" && !ret;
  const phase = needsPickup ? "pickup" : needsReturn ? "return" : null;

  const submit = async () => {
    if (!inspectionPhotosComplete(photos)) {
      toast.error("Capture all 6 angles before submitting");
      return;
    }
    const odoVal = parseInt(odometer, 10);
    if (!Number.isFinite(odoVal) || odoVal < 0) {
      toast.error("Enter the odometer reading in km");
      return;
    }
    if (phase === "pickup") {
      const fuelVal = parseInt(fuel, 10);
      if (!Number.isFinite(fuelVal) || fuelVal < 0 || fuelVal > 100) {
        toast.error("Enter fuel or battery charge % (0–100) before pickup");
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/inspections`, {
        phase,
        booking_id: booking.booking_id,
        photos: inspectionPhotosPayload(photos),
        odometer_km: odoVal,
        fuel_pct: fuel ? parseInt(fuel, 10) : undefined,
        notes,
      }, { withCredentials: true });
      toast.success(phase === "pickup" ? "Pickup inspection complete — trip is now active" : "Return inspection submitted");
      setCapturing(false);
      setPhotos({});
      await load();
      onDone?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatInspectionSubmitError(d));
    } finally {
      setSubmitting(false);
    }
  };

  if (!needsPickup && !needsReturn) {
    const awaitingCloseout = booking.status === "active" && ret && !asHost;
    return (
      <div className="space-y-4 md:col-span-2" data-testid={`trip-inspection-done-${booking.booking_id}`}>
        {awaitingCloseout && (
          <div className="zc-card p-4 border border-[var(--zc-border)]" data-testid="trip-awaiting-closeout">
            <div className="eyebrow">Return complete</div>
            <p className="text-sm text-[var(--zc-text-2)] mt-1">
              Your return scan is submitted. The host will review and close the trip — you&apos;ll get a notification when it&apos;s marked complete and you can leave a review.
            </p>
          </div>
        )}
        {pickup && <InspectionReport inspection={pickup} showPlateDetails={asHost} />}
        {ret && <InspectionReport inspection={ret} showPlateDetails={asHost} />}
      </div>
    );
  }

  if (!capturing) {
    return (
      <div className="zc-card p-5 md:col-span-2" data-testid={`trip-inspection-cta-${booking.booking_id}`}>
        <div className="eyebrow">{phase === "pickup" ? "Before you drive" : "Before you return"}</div>
        <h3 className="zc-headline text-xl mt-1">
          {phase === "pickup" ? "Pickup condition check" : "Return condition check"}
        </h3>
        <p className="text-sm text-[var(--zc-text-2)] mt-2">
          {phase === "pickup"
            ? "Walk around the car and capture 6 guided photos. Z-Cars AI will flag dents, scratches, and interior wear — then unlock vehicle controls."
            : "Document the car's condition at return. AI compares against pickup and the host's listing baseline for fair damage assessment."}
        </p>
        {pickup && phase === "return" && (
          <div className="mt-4">
            <InspectionReport inspection={pickup} compact showPlateDetails={asHost} />
          </div>
        )}
        <button
          type="button"
          onClick={() => setCapturing(true)}
          className="mt-4 px-5 h-11 zc-btn-primary text-sm flex items-center gap-2"
          data-testid={`trip-inspection-start-${phase}`}
        >
          <ClipboardCheck size={14} /> Start {phase} inspection
        </button>
      </div>
    );
  }

  const handleRemoteSubmit = async () => {
    toast.success(phase === "pickup" ? "Pickup inspection complete — trip is now active" : "Return inspection submitted");
    setCapturing(false);
    setPhotos({});
    setQrToken(null);
    await load();
    onDone?.();
  };

  const mergePhotos = (incoming) => {
    if (!incoming || !Object.keys(incoming).length) return;
    setPhotos(prev => {
      const next = { ...prev };
      for (const [angle, entry] of Object.entries(incoming)) {
        if (entry?.file_id && (!prev[angle] || prev[angle].file_id !== entry.file_id)) {
          next[angle] = entry;
        }
      }
      return next;
    });
  };

  const isEv = booking?.vehicle_snapshot?.vehicle_type === "electric";

  return (
    <div className="zc-card p-5 md:col-span-2" data-testid={`trip-inspection-form-${phase}`}>
      <div className="eyebrow capitalize">{phase} inspection</div>
      {/* liveCaptureOnly requires in-page camera; same-device QR session link below is the mobile fallback when camera fails */}
      <InspectionQrHandoff
        phase={phase}
        bookingId={booking.booking_id}
        active={capturing}
        photos={photos}
        onPhotosSync={mergePhotos}
        onSubmitted={handleRemoteSubmit}
        onToken={setQrToken}
        testid={`trip-inspection-qr-${phase}`}
      />
      <VehicleInspectionCapture
        value={photos}
        onChange={setPhotos}
        qrToken={qrToken}
        liveCaptureOnly
        bookingId={booking.booking_id}
        inspectionPhase={phase}
        testid={`trip-insp-${phase}`}
      />
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-xs text-[var(--zc-text-3)]">Odometer (km) *</label>
          <input className="zc-input w-full h-10 px-3 text-sm mt-1" value={odometer} onChange={e => setOdometer(e.target.value)} placeholder="e.g. 20000" required inputMode="numeric" />
          <div className="zc-meta mt-1">Clear odometer reading in km</div>
        </div>
        <div>
          <label className="text-xs text-[var(--zc-text-3)]">
            {isEv ? "Battery charge %" : "Fuel %"}
            {phase === "pickup" ? " *" : ""}
          </label>
          <input className="zc-input w-full h-10 px-3 text-sm mt-1" value={fuel} onChange={e => setFuel(e.target.value)} placeholder={isEv ? "e.g. 80" : "e.g. 75"} inputMode="numeric" required={phase === "pickup"} />
        </div>
      </div>
      <textarea className="zc-input w-full p-3 text-sm mt-3 h-20" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes (existing scratch on rear bumper…)" />
      <div className="flex gap-2 mt-4">
        <button type="button" onClick={() => setCapturing(false)} className="px-4 h-11 zc-btn-ghost text-sm">Cancel</button>
        <button type="button" onClick={submit} disabled={submitting} className="px-5 h-11 zc-btn-primary text-sm flex items-center gap-2">
          {submitting ? <Loader size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
          Submit &amp; analyze
        </button>
      </div>
    </div>
  );
}
